/**
 * Назви цілей полів-зв'язків (bec368f0, рішення Архітектора 24.10, варіант (б)): сервер віддає їх
 * полем `refs` у відповіді списку й картки — по одному елементу на пару (ціль, id) з правами
 * глядача (область `lookup`, read-areas.md, розд. 4). Значення поля у `values` лишається UUID.
 *
 * Показ (permissions.md, SEC-044): є назва — назва з посиланням на картку; `visible: true` без
 * назви — «Без назви» з посиланням; `visible: false` — «Запис недоступний» без посилання. Елемента
 * немає (значення «було» в історії, чернетка, свіжа локальна зміна, старий сервер) — нейтральне
 * «Інший запис» без посилання: про доступ ми тут нічого не знаємо, тож і не стверджуємо (рев'ю
 * Архітектора на #710). UUID людина не бачить ніколи: окремого шляху до назви повз `refs` немає
 * (запит картки цілі брав би квоту людини після Т2).
 */
import type { FieldValue, MetaField, RecordTargetRef } from '@clientline/contracts';
import { platformText as t } from './text.ts';

/** Індекс назв (`RecordTargetRef` контракту) за парою (ціль, id). */
export type RefIndex = ReadonlyMap<string, RecordTargetRef>;

export const EMPTY_REFS: RefIndex = new Map();

const pairKey = (entity: string, id: string) => `${entity}\u0000${id}`;

/** Індекс з одного чи кількох наборів `refs` (порції списку). Пізніший елемент пари — свіжіший. */
export function refIndex(...lists: readonly (readonly RecordTargetRef[] | undefined)[]): RefIndex {
  const index = new Map<string, RecordTargetRef>();
  for (const list of lists) {
    for (const ref of list ?? []) index.set(pairKey(ref.entity, ref.id), ref);
  }
  return index;
}

/** Набір `refs` з доданими свіжими елементами: пара, що вже є, замінюється (без дублів). */
export function mergeRefs(
  base: readonly RecordTargetRef[] | undefined,
  fresh: readonly RecordTargetRef[],
): RecordTargetRef[] {
  return [...refIndex(base, fresh).values()];
}

/** Поле-зв'язок: UUID з ціллю в `/meta` (FLD-11; FieldInput, тип `pick`). */
export const isLookup = (field: MetaField | undefined): field is MetaField & { target: string } =>
  field?.type === 'uuid' && typeof field.target === 'string' && field.target !== '';

/**
 * Що показати замість UUID: посилання на картку цілі; `hidden` — сервер сказав «недоступний»;
 * `unknown` — елемента в `refs` немає, доступ невідомий. Обидва — без посилання.
 */
export type LookupShown =
  | { kind: 'link'; entity: string; id: string; text: string }
  | { kind: 'hidden' | 'unknown'; text: string };

export const lookupText = {
  unavailable: 'Запис недоступний',
  unknown: 'Інший запис',
  untitled: t.untitled,
} as const;

/** Показ значення поля-зв'язку; порожнє значення — `null` (екран пише своє «не вказано»). */
export function lookupShown(
  field: MetaField & { target: string },
  value: FieldValue | undefined,
  refs: RefIndex,
): LookupShown | null {
  if (value === null || value === undefined || value === '') return null;
  const ref = typeof value === 'string' ? refs.get(pairKey(field.target, value)) : undefined;
  if (!ref) return { kind: 'unknown', text: lookupText.unknown };
  if (!ref.visible) return { kind: 'hidden', text: lookupText.unavailable };
  const label = ref.label?.trim();
  return { kind: 'link', entity: ref.entity, id: ref.id, text: label || lookupText.untitled };
}

/**
 * Значення полів-зв'язків, яких ще немає в `refs`: після успішного запису (`values` відповіді —
 * усі поля, зокрема підставлені сервером правилом `same`) з уже злитими `refs` відповіді. Відповідь
 * `PATCH` і переходу несе `refs` (cb04620c), тож це запасний шлях — повтор ключа ідемпотентності,
 * старий сервер чи відповідь без `refs`: картку треба перечитати, інакше нова ціль показувалась би
 * «Інший запис» (доповнення Архітектора 24.10).
 */
export function missingRefs(
  fields: readonly MetaField[],
  values: Readonly<Record<string, FieldValue | undefined>>,
  refs: RefIndex,
): boolean {
  return fields.some((field) => {
    if (!isLookup(field) || !(field.key in values)) return false;
    const value = values[field.key];
    return typeof value === 'string' && value !== '' && !refs.has(pairKey(field.target, value));
  });
}
